import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import StartScan from './StartScan';

import QuitePlace from 'assets/images/voice-scan/quiet-place.png';
import VoiceOnly from 'assets/images/voice-scan/voice-only.png';
import SpeakNaturally from 'assets/images/voice-scan/speak-naturally.png';
import VoiceScanInitial from 'assets/images/voice-scan/voice-scan-initial.png';

export default function StartVoiceScanPage() {
  const [startScan, setStartScan] = useState(true);
  const [showInitial, setShowInitial] = useState(false);

  if (startScan) {
    return <StartScan />;
  }

  if (showInitial) {
    return <ScanInitial onStart={() => setStartScan(true)} />;
  }

  return <ScanInstruction onClose={() => setShowInitial(true)} />;
}

function ScanInitial({ onStart }: { onStart: () => void }) {
  const { t } = useTranslation();

  const INSTRUCTIONS = [
    {
      title: t('voice_initial_title_1'),
      desc: t('voice_initial_desc_1'),
    },
    {
      title: t('voice_initial_title_2'),
      desc: t('voice_initial_desc_2'),
    },
    {
      title: t('voice_initial_title_3'),
      desc: t('voice_initial_desc_3'),
    },
  ];

  return (
    <div className='grid md:grid-cols-2 items-center justify-center h-full lg:h-[calc(100vh-8rem)] max-w-5xl mx-auto my-6 gap-6'>
      <div className='w-full h-40 overflow-hidden md:h-2/3'>
        <img
          src={VoiceScanInitial}
          alt='initial'
          className='object-contain w-full h-full'
        />
      </div>
      <div className='w-full px-4 md:w-4/5'>
        <div className='grid gap-4 mb-12 md:gap-5'>
          {INSTRUCTIONS.map(({ title, desc }, index) => (
            <div key={index}>
              <p className='text-lg font-semibold leading-tight'>{title}</p>
              <p className=''>{desc}</p>
            </div>
          ))}
        </div>
        <Button onClick={onStart} className='w-full'>
          {t('get_started')}
        </Button>
      </div>
    </div>
  );
}

function ScanInstruction({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();

  const INSTRUCTIONS = [
    {
      title: t('voice_step_title_1'),
      desc: t('voice_step_desc_1'),
      img: QuitePlace,
    },
    {
      title: t('voice_step_title_2'),
      desc: t('voice_step_desc_2'),
      img: VoiceOnly,
    },
    {
      title: t('voice_step_title_3'),
      desc: t('voice_step_desc_3'),
      img: SpeakNaturally,
      size: 260,
    },
  ];

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  return (
    <div className='flex items-center justify-center h-fit md:h-[calc(100vh-10rem)]  lg:h-[calc(100vh-8rem)] max-w-sm mx-auto mt-4 mb-8'>
      <div className='px-6 space-y-4 sm:px-0'>
        <div>
          <div className='mx-auto overflow-hidden size-80'>
            <img
              src={INSTRUCTIONS[currentIndex].img}
              alt='image'
              className='object-contain w-full'
              style={{
                height: INSTRUCTIONS[currentIndex].size
                  ? `${INSTRUCTIONS[currentIndex].size}px`
                  : '100%',
              }}
            />
          </div>
          <div className='h-[72px]'>
            <p className='font-medium leading-tight text-center'>
              {INSTRUCTIONS[currentIndex].title}
            </p>
            <p className='text-center'>{INSTRUCTIONS[currentIndex].desc}</p>
          </div>
        </div>
        <div className='flex flex-col justify-center gap-4 sm:flex-row'>
          <Button
            variant='outline'
            className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
            disabled={currentIndex === 0}
            onClick={() => {
              setCurrentIndex((prev) => prev - 1);
            }}
          >
            {t('previous')}
          </Button>
          <Button
            size='sm'
            className='h-8 px-6 rounded-full'
            onClick={() => {
              if (currentIndex === INSTRUCTIONS.length - 1) onClose();
              setCurrentIndex((prev) => prev + 1);
            }}
          >
            {t('next')}
          </Button>
          <Button
            variant='outline'
            className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
            onClick={onClose}
          >
            {t('skip')}
          </Button>
        </div>
      </div>
    </div>
  );
}
